/**
 * Eva per-USER monthly budget-override API client (SF-3825 / SF-3738).
 *
 * Wraps the admin per-user budget-override endpoints on sf-api so the
 * company-detail page's per-user inline editor can READ a user's effective
 * monthly cap and WRITE (upsert / revert) the per-user override without
 * re-deriving paths or response types inline. Mirrors evaBudgetClient.ts (the
 * per-company equivalent) one-to-one — same envelope, same money-as-string
 * contract, same suppressed-toast policy — just keyed by userId.
 *
 *   GET /admin/eva/user-budget-override/:userId  — perm admin:eva:budget:read
 *   PUT /admin/eva/user-budget-override/:userId  — perm admin:eva:budget:write
 *     body { monthlyCostLimitUsd: number|null, note?: string }
 *     number → upsert override; null → revert to env default.
 *
 * ⚠️ sf-api wraps every response in `{ success, data }`. The Refine
 * `dataProvider.custom` unwraps the sf-api envelope and returns `{ data:
 * innerPayload }`, so `useCustom` exposes the inner payload via
 * `query.data?.data` (same convention as the usage hooks). Money values are
 * fixed-precision STRINGS to avoid float drift — parse only at the edge.
 */
import { useCustom, useCustomMutation } from '@refinedev/core';

import {
  EVA_BUDGET_READ_PERMISSION,
  EVA_BUDGET_WRITE_PERMISSION,
  type EvaBudgetUpdate,
} from './evaBudgetClient';

/** Base path for the per-USER Eva budget-override endpoints on sf-api. */
export const EVA_USER_BUDGET_BASE = 'admin/eva/user-budget-override';

// Per-user writes are gated on the SAME permissions as the per-company budget
// (admin:eva:budget:read / :write) — re-export so callers import one source.
export { EVA_BUDGET_READ_PERMISSION, EVA_BUDGET_WRITE_PERMISSION };

/**
 * Effective-cap envelope returned by GET/PUT (mirrors sf-api
 * services/insights/evaUserBudgetOverrideService.js `shapeOverride`). Identical
 * shape to the per-company {@link EvaBudgetOverride} but keyed by `userId`.
 *
 * - `effectiveCapUsd`: the cap actually enforced — the override value when one
 *   is set, else the configured env default. Always a string.
 * - `monthlyCostLimitUsd`: the override value as a string when an override
 *   exists, else null (no per-user override → running on the default).
 * - `isOverride`: whether a per-user override doc currently exists.
 */
export interface EvaUserBudgetOverride {
  userId: string;
  monthlyCostLimitUsd: string | null;
  effectiveCapUsd: string;
  isOverride: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  note: string | null;
}

/** Common options shared by the user-budget hooks. */
interface EnabledOption {
  /** Gate the request (e.g. on access-control resolution). Defaults to true. */
  enabled?: boolean;
}

/**
 * Fetch a user's effective monthly Eva cap + override state. Disabled when
 * `userId` is empty so a row editor can mount before an id is available without
 * firing a request. Returns the inner payload via `query.data?.data` (sf-api
 * envelope unwrapped by the data provider).
 * @param userId - user ObjectId
 */
export function useUserBudgetOverride(userId: string | null | undefined, options: EnabledOption = {}) {
  return useCustom<EvaUserBudgetOverride>({
    url: `/${EVA_USER_BUDGET_BASE}/${userId}`,
    method: 'get',
    queryOptions: {
      enabled: (options.enabled ?? true) && !!userId,
      // Match the per-company control: don't re-seed the edit field on window
      // focus and silently discard an unsaved cap edit.
      refetchOnWindowFocus: false,
    },
    errorNotification: false,
  });
}

/**
 * Mutation to upsert (number) or revert (null) a user's monthly cap. The caller
 * supplies the userId + body at call time; on success the page refetches so the
 * roster's effective-cap % reflects the new cap. Errors (incl. a 403 for callers
 * lacking `admin:eva:budget:write`) are surfaced by the caller, so the global
 * error toast is suppressed via {@link userBudgetUpdateMutationArgs}.
 */
export function useSetUserBudgetOverride() {
  return useCustomMutation<EvaUserBudgetOverride>();
}

/**
 * Build the PUT mutation args for a per-user budget-override write. Extracted so
 * callers and tests share one source of truth for the URL + method + body shape.
 * @param userId - user ObjectId
 * @param body - the update payload (number upserts, null reverts)
 */
export function userBudgetUpdateMutationArgs(userId: string, body: EvaBudgetUpdate) {
  return {
    url: `/${EVA_USER_BUDGET_BASE}/${userId}`,
    method: 'put' as const,
    values: body,
    // Failures are surfaced inline by the editor (esp. 403); no global toast.
    errorNotification: false as const,
    successNotification: false as const,
  };
}
